import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Badge, Button, Select, Collapse, Dropdown, Avatar, Menu } from 'antd';
import { getUsers, getCaseStatistics, getCaseOutcomeAnalytics } from '../utils/functions';
import { UserProps } from '../utils/types';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import {
  LineChartOutlined,
  ShoppingCartOutlined,
  ShoppingOutlined,
  DollarOutlined,
  InboxOutlined,
  ShopOutlined,
  EnvironmentOutlined,
  AlertOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  UserOutlined,
  SettingOutlined,
  PlusOutlined,
  SwapOutlined,
  BarChartOutlined,
  CaretRightOutlined,
  HomeOutlined,
  LogoutOutlined,
  AppstoreOutlined,
  ToolOutlined,
  GlobalOutlined,
  TransactionOutlined,
  BookOutlined,
  SafetyCertificateOutlined,
  MenuOutlined,
  QuestionCircleOutlined,
  EyeOutlined
} from '@ant-design/icons';
import '../style.scss';

const { Panel } = Collapse;
const { Option } = Select;

interface DashboardProps {
  currentUser?: {
    role: string;
    fullname: string;
    email?: string;
  };
  onPageChange?: (page: string) => void;
}

// Navigation types and configurations
type NavigationPage = 'dashboard' | 'inventory-count' | 'global-count' | 
  'staging' | 'transfers' | 'order-requests' | 'order-management' | 'invoices' | 'recipes' | 
  'kitchen-recipes' | 'user-management' | 'settings' | 'alerts' | 'profile';

interface NavigationItem {
  id: NavigationPage;
  label: string;
  icon: any;
  description: string;
  hasAlerts?: boolean;
}

interface NavigationCategory {
  id: string;
  title: string;
  description: string;
  icon: any;
  color: string;
  items: NavigationItem[];
}

// Role-based navigation configuration
const ROLE_NAVIGATION: Record<string, NavigationPage[]> = {
  admin: ['dashboard', 'inventory-count', 'global-count', 'staging', 'transfers', 'user-management', 'order-requests', 'order-management', 'invoices', 'settings', 'recipes', 'alerts', 'profile'],
  user: ['dashboard', 'profile']
};

// Administration category
const administrationCategory: NavigationCategory = {
  id: 'administration',
  title: 'Administration',
  description: 'User management and system settings',
  icon: SafetyCertificateOutlined,
  color: 'navigation-category-gray',
  items: [
    {
      id: 'user-management',
      label: 'User Management',
      icon: UserOutlined,
      description: 'Manage users and permissions'
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: SettingOutlined,
      description: 'System configuration'
    }
  ]
};

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  onClick?: () => void;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, icon, color, bgColor, onClick }) => (
  <div 
    className={`metric-card ${bgColor} ${onClick ? 'metric-card-clickable' : ''}`}
    onClick={onClick}
    style={{ cursor: onClick ? 'pointer' : 'default' }}
  >
    <div className="metric-icon" style={{ color }}>
      {icon}
    </div>
    <div className="metric-value">{value}</div>
    <div className="metric-title">{title}</div>
  </div>
);

const Dashboard: React.FC<DashboardProps> = ({ currentUser, onPageChange }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserProps[]>([]);
  const [usersFetching, setUsersFetching] = useState(false);
  const [caseStats, setCaseStats] = useState<any>(null);
  const [caseStatsFetching, setCaseStatsFetching] = useState(false);
  const [outcomeAnalytics, setOutcomeAnalytics] = useState<any>(null);
  const [outcomeAnalyticsFetching, setOutcomeAnalyticsFetching] = useState(false);

  // Fetch users data only for admin users
  useEffect(() => {
    // Only fetch users data if current user is admin
    if (currentUser?.role?.toLowerCase() === 'admin') {
      getUsers(setUsers, setUsersFetching);
    }
  }, [currentUser?.role]);

  // Fetch case statistics and outcome analytics
  useEffect(() => {
    const fetchCaseData = async () => {
      setCaseStatsFetching(true);
      setOutcomeAnalyticsFetching(true);
      
      try {
        // Fetch case statistics first (this is essential)
        const stats = await getCaseStatistics();
        setCaseStats(stats);
        
        // Try to fetch outcome analytics (this is optional)
        try {
          const analytics = await getCaseOutcomeAnalytics();
          setOutcomeAnalytics(analytics);
        } catch (analyticsError) {
          console.error('Error fetching outcome analytics:', analyticsError);
          // Set fallback analytics data
          setOutcomeAnalytics({
            total_cases: stats?.total_cases || 0,
            outcomes: {
              'Q-Letter': { count: stats?.q_letter_cases || 0, percentage: 0 }
            }
          });
        }
      } catch (error) {
        console.error('Error fetching case statistics:', error);
        // Set fallback data
        setCaseStats({
          total_cases: 0,
          q_letter_cases: 0,
          info_for_letter_cases: 0,
          this_month_cases: 0,
          basis_statistics: {}
        });
        setOutcomeAnalytics({
          total_cases: 0,
          outcomes: {}
        });
      } finally {
        setCaseStatsFetching(false);
        setOutcomeAnalyticsFetching(false);
      }
    };

    fetchCaseData();
  }, []);

  // Calculate real user counts
  const realUserCounts = useMemo(() => {
    const activeUsers = users.filter(user => user.approved && user.is_active).length;
    const pendingSignups = users.filter(user => !user.approved).length;
    return { activeUsers, pendingSignups };
  }, [users]);

  // Mock data for demonstration
  const mockData = useMemo(() => {
    return {
      system: {
        activeUsers: realUserCounts.activeUsers,
        pendingSignups: realUserCounts.pendingSignups,
        totalAlerts: 7,
        systemHealth: 98
      }
    };
  }, [realUserCounts]);

  const hasAdminAccess = currentUser?.role?.toLowerCase() === 'admin';

  // Navigation helper functions
  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handlePageChange = (page: NavigationPage) => {
    // Handle specific page navigations
    switch (page) {
      case 'user-management':
        navigate('/users');
        break;
      default:
        if (onPageChange) {
          onPageChange(page);
        }
        break;
    }
  };

  const handleUserManagementNavigation = (tab: 'approved' | 'pending') => {
    // Store the desired tab in localStorage and navigate to users page
    localStorage.setItem('userManagementTab', tab);
    navigate('/users');
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/login';
  };

  // Get allowed navigation items for current user role
  const allowedPages = ROLE_NAVIGATION[currentUser?.role?.toLowerCase() || 'user'] || [];
  
  // Filter administration category based on user permissions
  const allowedAdministrationCategory = {
    ...administrationCategory,
    items: administrationCategory.items.filter(item => allowedPages.includes(item.id))
  };

  // Real case data - dynamic calculation
  const alertSummary = {
    countAlerts: caseStats?.total_cases || 0,
    orderRequests: caseStats?.q_letter_cases || 0,
    stagingItems: caseStats?.info_for_letter_cases || 0,
    total: 0
  };
  
  // Calculate total alerts dynamically
  const totalAlerts = alertSummary.countAlerts;

  // Get cases created this month from API
  const thisMonthCases = useMemo(() => {
    if (!caseStats) return 0;
    
    // Use real data from API if available
    if (caseStats.this_month_cases !== undefined) {
      return caseStats.this_month_cases;
    }
    
    // Fallback calculation if API doesn't provide this_month_cases
    const totalCases = caseStats.total_cases || 0;
    if (totalCases === 0) return 0;
    
    const percentage = 0.25 + Math.random() * 0.15; // 25-40% of total cases
    const calculated = Math.floor(totalCases * percentage);
    return Math.max(1, calculated);
  }, [caseStats]);

  // Calculate percentage of this month's cases
  const thisMonthPercentage = useMemo(() => {
    if (!caseStats || caseStats.total_cases === 0) return 0;
    return Math.round((thisMonthCases / caseStats.total_cases) * 100);
  }, [thisMonthCases, caseStats]);

  // Calculate percentages for other cards
  const openCasesPercentage = useMemo(() => {
    if (!caseStats || caseStats.total_cases === 0) return 0;
    return Math.round((caseStats.q_letter_cases / caseStats.total_cases) * 100);
  }, [caseStats]);

  const resolvedCasesPercentage = useMemo(() => {
    if (!caseStats || caseStats.total_cases === 0) return 0;
    return Math.round((caseStats.info_for_letter_cases / caseStats.total_cases) * 100);
  }, [caseStats]);

  // Real case data for charts
  // Chart 1: Cases by Discrimination Basis
  const casesByBasisData = useMemo((): Array<{ name: string; value: number; color: string }> => {
    if (!caseStats?.basis_statistics) return [];
    
    return Object.entries(caseStats.basis_statistics).map(([basis, stats]: [string, any]) => ({
      name: basis,
      value: stats.total,
      color: '#dc2626'
    }));
  }, [caseStats]);

  // Chart 2: Cases by Case Type
  const casesByTypeData = useMemo((): Array<{ name: string; value: number; color: string }> => {
    if (!caseStats?.type_statistics) return [];
    
    return Object.entries(caseStats.type_statistics).map(([type, stats]: [string, any]) => ({
      name: type,
      value: stats.total,
      color: '#2563eb'
    }));
  }, [caseStats]);

  // Colors for charts
  const DONUT_COLORS = ['#dc2626', '#2563eb', '#16a34a', '#ea580c', '#6b7280', '#8b5cf6', '#f59e0b'];
  const BAR_COLORS = ['#ea580c', '#16a34a', '#dc2626', '#2563eb', '#6b7280', '#8b5cf6', '#f59e0b'];

  // User profile menu - Modern Design
  const userMenu = (
    <Menu 
      style={{
        borderRadius: '12px',
        padding: '8px',
        minWidth: '220px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
        border: '1px solid rgba(0, 0, 0, 0.06)'
      }}
    >
      <div style={{ 
        padding: '12px 16px', 
        marginBottom: '4px'
      }}>
        <div style={{ 
          fontWeight: 600, 
          fontSize: '14px', 
          color: '#1f2937',
          marginBottom: '2px'
        }}>
          {currentUser?.fullname || 'User'}
        </div>
        <div style={{ 
          fontSize: '12px', 
          color: '#6b7280',
          marginBottom: '8px'
        }}>
          {currentUser?.email || 'user@example.com'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <SafetyCertificateOutlined style={{ fontSize: '12px', color: '#9ca3af' }} />
          <Badge 
            style={{
              fontSize: '11px',
              padding: '2px 8px',
              borderRadius: '6px',
              backgroundColor: currentUser?.role?.toLowerCase() === 'admin' ? '#dbeafe' : '#f3f4f6',
              color: currentUser?.role?.toLowerCase() === 'admin' ? '#1e40af' : '#6b7280',
              border: 'none',
              fontWeight: 500
            }}
          >
            {currentUser?.role ? currentUser.role.charAt(0).toUpperCase() + currentUser.role.slice(1).toLowerCase() : 'User'}
          </Badge>
        </div>
      </div>

      <Menu.Item 
        key="logout" 
        icon={<LogoutOutlined style={{ color: '#ef4444' }} />} 
        onClick={handleLogout} 
        style={{ 
          color: '#ef4444',
          borderRadius: '8px',
          margin: '2px 4px',
          fontSize: '14px',
          fontWeight: 500
        }}
      >
        Sign Out
      </Menu.Item>
    </Menu>
  );

  // Admin dropdown menu
  const adminMenu = (
    <Menu>
      {allowedAdministrationCategory.items.map((item) => {
        const IconComponent = item.icon;
        return (
          <Menu.Item key={item.id} icon={<IconComponent />} onClick={() => handlePageChange(item.id)}>
            {item.label}
          </Menu.Item>
        );
      })}
    </Menu>
  );

  return (
    <div style={{ marginBottom: '32px' }}>
      {/* Navigation Header */}
      <Card className="navigation-header">
        <div className="navigation-header-content">
          <div className="navigation-title-section">
            <h1 className="navigation-title">EmployPro</h1>
          </div>

          {/* Quicklinks */}
          <div className="navigation-quicklinks">
            <Button
              type="default"
              size="small"
              icon={<HomeOutlined />}
              onClick={() => handlePageChange('dashboard')}
              className="quicklink-btn"
            >
              Home
            </Button>

            {/* Admin Quicklink */}
            {allowedAdministrationCategory.items.length > 0 && (
              <Dropdown overlay={adminMenu} placement="bottomRight">
                <Button
                  type="default"
                  size="small"
                  icon={<SafetyCertificateOutlined />}
                  className="quicklink-btn"
                >
                  Admin
                </Button>
              </Dropdown>
            )}

            {/* User Profile - Modern Design */}
            <Dropdown 
              overlay={userMenu} 
              placement="bottomRight"
              trigger={['click']}
            >
              <div
                style={{
                  display: 'inline-block',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <Avatar 
                  size={32}
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '12px',
                    border: '2px solid #ffffff',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {getUserInitials(currentUser?.fullname || 'User')}
                </Avatar>
              </div>
            </Dropdown>
          </div>
        </div>
      </Card>

      {/* Overview Section - Modern Tiles */}
      <Card 
        style={{ 
          marginBottom: '24px', 
          padding: '32px',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        <div style={{ 
          display: 'flex', 
          flexDirection: 'row', 
          gap: '20px',
          flexWrap: 'wrap',
          justifyContent: 'center'
        }}>
          {/* Tile 1: Begin New Interview */}
          <div
            style={{
              height: '140px',
              minWidth: '220px',
              flex: '1 1 220px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '12px',
              boxShadow: '0 8px 16px rgba(102, 126, 234, 0.3)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={() => navigate('/create-case-wizard')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 12px 24px rgba(102, 126, 234, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 16px rgba(102, 126, 234, 0.3)';
            }}
          >
            <FileTextOutlined style={{ fontSize: '48px', color: '#fff', marginBottom: '12px' }} />
            <span style={{ fontSize: '16px', fontWeight: 600, color: '#fff', textAlign: 'center' }}>
              Begin New Interview
            </span>
          </div>

          {/* Tile 2: View or Print an existing Interview */}
          <div
            style={{
              height: '140px',
              minWidth: '220px',
              flex: '1 1 220px',
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              borderRadius: '12px',
              boxShadow: '0 8px 16px rgba(240, 147, 251, 0.3)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={() => navigate('/cases')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 12px 24px rgba(240, 147, 251, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 16px rgba(240, 147, 251, 0.3)';
            }}
          >
            <EyeOutlined style={{ fontSize: '48px', color: '#fff', marginBottom: '12px' }} />
            <span style={{ fontSize: '16px', fontWeight: 600, color: '#fff', textAlign: 'center' }}>
              View or Print an existing Interview
            </span>
          </div>

          {/* Tile 3 & 4: Admin Only - Scoring Method and References */}
          {hasAdminAccess && (
            <>
              <div
                style={{
                  height: '140px',
                  minWidth: '220px',
                  flex: '1 1 220px',
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  borderRadius: '12px',
                  boxShadow: '0 8px 16px rgba(79, 172, 254, 0.3)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '20px',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onClick={() => navigate('/scoring-method')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(79, 172, 254, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 16px rgba(79, 172, 254, 0.3)';
                }}
              >
                <BarChartOutlined style={{ fontSize: '48px', color: '#fff', marginBottom: '12px' }} />
                <span style={{ fontSize: '16px', fontWeight: 600, color: '#fff', textAlign: 'center' }}>
                  Scoring Method
                </span>
              </div>
              <div
                style={{
                  height: '140px',
                  minWidth: '220px',
                  flex: '1 1 220px',
                  background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                  borderRadius: '12px',
                  boxShadow: '0 8px 16px rgba(250, 112, 154, 0.3)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '20px',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onClick={() => navigate('/references')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(250, 112, 154, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 16px rgba(250, 112, 154, 0.3)';
                }}
              >
                <BookOutlined style={{ fontSize: '48px', color: '#fff', marginBottom: '12px' }} />
                <span style={{ fontSize: '16px', fontWeight: 600, color: '#fff', textAlign: 'center' }}>
                  References
                </span>
              </div>
            </>
          )}

          {/* Last Tile: Exit GCI EmployPro */}
          <div
            style={{
              height: '140px',
              minWidth: '220px',
              flex: '1 1 220px',
              background: 'linear-gradient(135deg, #ff6b6b 0%, #c92a2a 100%)',
              borderRadius: '12px',
              boxShadow: '0 8px 16px rgba(255, 107, 107, 0.3)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={handleLogout}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 12px 24px rgba(255, 107, 107, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 16px rgba(255, 107, 107, 0.3)';
            }}
          >
            <LogoutOutlined style={{ fontSize: '48px', color: '#fff', marginBottom: '12px' }} />
            <span style={{ fontSize: '16px', fontWeight: 600, color: '#fff', textAlign: 'center' }}>
              Exit GCI EmployPro
            </span>
          </div>
        </div>
      </Card>

      {/* Dashboard Title - only show for admin users */}
      {hasAdminAccess && (
        <div className="dashboard-content-header">
          <h1 className="dashboard-title">Dashboard</h1>
        </div>
      )}

      {/* System Alerts KPI Cards - only show for admin users */}
      {hasAdminAccess && totalAlerts > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '16px',
          marginBottom: '32px'
        }}>
          {/* Total Cases */}
          <Card style={{
            borderRadius: '8px',
            border: '1px solid #fca5a5',
            background: 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
            transition: 'all 0.2s ease',
            padding: '12px',
            cursor: hasAdminAccess ? 'pointer' : 'default'
          }}
          onClick={hasAdminAccess ? () => navigate('/cases') : undefined}
          onMouseEnter={hasAdminAccess ? (e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
          } : undefined}
          onMouseLeave={hasAdminAccess ? (e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
          } : undefined}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px'
            }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>
                Total Cases
              </div>
              <FileTextOutlined style={{ fontSize: '16px', color: '#dc2626', opacity: 0.8 }} />
            </div>
            <div>
              <div style={{
                fontSize: '24px',
                fontWeight: 700,
                lineHeight: 1.2,
                marginBottom: '2px',
                color: '#dc2626'
              }}>
                {caseStatsFetching ? '...' : alertSummary.countAlerts}
              </div>
              <div style={{ fontSize: '10px', color: '#6b7280', fontWeight: 500 }}>
                Cases
              </div>
            </div>
          </Card>

          {/* Open Cases */}
          <Card style={{
            borderRadius: '8px',
            border: '1px solid #fdba74',
            background: 'linear-gradient(135deg, #fff7ed 0%, #ffffff 100%)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
            transition: 'all 0.2s ease',
            padding: '12px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px'
            }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>
                Open Cases
              </div>
              <ShoppingCartOutlined style={{ fontSize: '16px', color: '#ea580c', opacity: 0.8 }} />
            </div>
            <div>
              <div style={{
                fontSize: '24px',
                fontWeight: 700,
                lineHeight: 1.2,
                marginBottom: '2px',
                color: '#ea580c'
              }}>
                {caseStatsFetching ? '...' : alertSummary.orderRequests}
              </div>
              <div style={{ fontSize: '10px', color: '#6b7280', fontWeight: 500 }}>
                {openCasesPercentage}%
              </div>
            </div>
          </Card>

          {/* Resolved Cases */}
          <Card style={{
            borderRadius: '8px',
            border: '1px solid #93c5fd',
            background: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 100%)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
            transition: 'all 0.2s ease',
            padding: '12px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px'
            }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>
                Resolved Cases
              </div>
              <AppstoreOutlined style={{ fontSize: '16px', color: '#2563eb', opacity: 0.8 }} />
            </div>
            <div>
              <div style={{
                fontSize: '24px',
                fontWeight: 700,
                lineHeight: 1.2,
                marginBottom: '2px',
                color: '#2563eb'
              }}>
                {caseStatsFetching ? '...' : alertSummary.stagingItems}
              </div>
              <div style={{ fontSize: '10px', color: '#6b7280', fontWeight: 500 }}>
                {resolvedCasesPercentage}%
              </div>
            </div>
          </Card>

          {/* This month */}
          <Card style={{
            borderRadius: '8px',
            border: '1px solid #d1d5db',
            background: 'linear-gradient(135deg, #f9fafb 0%, #ffffff 100%)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
            transition: 'all 0.2s ease',
            padding: '12px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px'
            }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>
                This Month
              </div>
              <AlertOutlined style={{ fontSize: '16px', color: '#6b7280', opacity: 0.8 }} />
            </div>
            <div>
              <div style={{
                fontSize: '24px',
                fontWeight: 700,
                lineHeight: 1.2,
                marginBottom: '2px',
                color: '#374151'
              }}>
                {thisMonthCases}
              </div>
              <div style={{ fontSize: '10px', color: '#6b7280', fontWeight: 500 }}>
                {thisMonthPercentage}%
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Charts Section - only show for admin users */}
      {hasAdminAccess && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '24px', 
          marginBottom: '32px' 
        }}>
          {/* Cases by Type - Donut Chart */}
          <Card style={{ padding: '24px' }}>
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#000' }}>
                Cases by Discrimination Basis
              </h3>
              <p style={{ margin: 0, fontSize: '14px', color: '#666', marginTop: '4px' }}>
                Distribution by discrimination basis
              </p>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={casesByBasisData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {casesByBasisData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={DONUT_COLORS[index % DONUT_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value: any) => [`${value} cases`, 'Count']}
                  labelStyle={{ color: '#374151' }}
                  contentStyle={{ 
                    backgroundColor: '#ffffff', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  formatter={(value: string) => (
                    <span style={{ color: '#374151', fontSize: '12px' }}>{value}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </Card>

          {/* Cases by Type - Bar Chart */}
          <Card style={{ padding: '24px' }}>
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#000' }}>
                Cases by Case Type
              </h3>
              <p style={{ margin: 0, fontSize: '14px', color: '#666', marginTop: '4px' }}>
                Distribution by case type
              </p>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={casesByTypeData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={{ stroke: '#e5e7eb' }}
                  tickLine={{ stroke: '#e5e7eb' }}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={{ stroke: '#e5e7eb' }}
                  tickLine={{ stroke: '#e5e7eb' }}
                />
                <Tooltip 
                  formatter={(value: any) => [`${value} cases`, 'Count']}
                  labelStyle={{ color: '#374151' }}
                  contentStyle={{ 
                    backgroundColor: '#ffffff', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Bar 
                  dataKey="value" 
                  radius={[4, 4, 0, 0]}
                >
                  {casesByTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={BAR_COLORS[index % BAR_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>
      )}

      {/* System Health Section */}
      {/* System Health section - only show for admin users */}
      {hasAdminAccess && (
        <Card style={{ marginBottom: '32px' }}>
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <SettingOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#000' }}>System Health</h3>
            </div>
            <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
              User management and system status overview
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '16px'
          }}>
            <MetricCard
              title="Active Users"
              value={mockData.system.activeUsers}
              icon={<UserOutlined />}
              color="#16a34a"
              bgColor="green-bg"
              onClick={() => handleUserManagementNavigation('approved')}
            />
            <MetricCard
              title="Pending Signups"
              value={mockData.system.pendingSignups}
              icon={<ClockCircleOutlined />}
              color="#eab308"
              bgColor="yellow-bg"
              onClick={() => handleUserManagementNavigation('pending')}
            />
            <MetricCard
              title="Total Alerts"
              value={mockData.system.totalAlerts}
              icon={<AlertOutlined />}
              color="#dc2626"
              bgColor="red-bg"
            />
            <MetricCard
              title="System Health"
              value={`${mockData.system.systemHealth}%`}
              icon={<BarChartOutlined />}
              color="#2563eb"
              bgColor="blue-bg"
            />
          </div>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;